
import React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";

import PhoneIcon from "@mui/icons-material/Phone";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HomeIcon from "@mui/icons-material/Home";

const InfoCard = ({ icon, title, children }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 3,
      background: "#f8fafc",
      border: "1px solid #e5e7eb",
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
      {icon}
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={700}
      >
        {title}
      </Typography>
    </Stack>
    {children}
  </Box>
);

const formatDate = (date) => {
  if (!date) return "Ongoing";

  const parsedDate = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return "Ongoing";

  return parsedDate.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatAmount = (amount) => {
  if (amount === null || amount === undefined || amount === "") {
    return "—";
  }

  const value = Number(amount);

  if (!Number.isFinite(value)) return "—";

  return `₹${value.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
};

const formatBillMonth = (billMonth) => {
  if (!billMonth) return "—";

  const [year, month] = String(billMonth).split("-");

  if (!year || !month) return billMonth;

  const date = new Date(Number(year), Number(month) - 1, 1);

  if (Number.isNaN(date.getTime())) return billMonth;

  return date.toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

const getPaymentChip = (status) => {
  switch (String(status ?? "").toUpperCase()) {
    case "COMPLETED":
      return { label: "Paid", color: "success" };

    case "PARTIALLY_COMPLETED":
      return { label: "Partially Paid", color: "warning" };

    case "NOT_STARTED":
      return { label: "Pending", color: "error" };

    default:
      return { label: status || "Unknown", color: "default" };
  }
};

const TenantDialog = ({ selectedTenant, setSelectedTenant }) => {
  if (!selectedTenant) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let statusLabel = "Active";
  let statusColor = "primary";

  if (
    selectedTenant.group === "food" ||
    selectedTenant.accommodationType === "FOOD_ONLY"
  ) {
    statusLabel = "Food Only";
    statusColor = "warning";
  } else if (
    selectedTenant.startDate &&
    new Date(selectedTenant.startDate) > today
  ) {
    statusLabel = "Upcoming";
    statusColor = "success";
  } else if (
    selectedTenant.endDate &&
    new Date(selectedTenant.endDate) < today
  ) {
    statusLabel = "Completed";
    statusColor = "default";
  }

  const accommodation = (
    selectedTenant.accommodationType || "N/A"
  ).replaceAll("_", " ");

  const location =
    selectedTenant.group === "food"
      ? "Food Only"
      : selectedTenant.yLabel?.split("__")[0] || "Not specified";

  const monthlyFees = Array.isArray(
    selectedTenant.monthlyFeeStatusDTOs
  )
    ? [...selectedTenant.monthlyFeeStatusDTOs].sort((a, b) =>
        String(b.billMonth ?? "").localeCompare(
          String(a.billMonth ?? "")
        )
      )
    : [];

  return (
    <Dialog
      open={Boolean(selectedTenant)}
      onClose={() => setSelectedTenant(null)}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
          color: "#fff",
          px: { xs: 2, sm: 3 },
          py: 2.5,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            minWidth={0}
          >
            <Avatar
              sx={{
                width: { xs: 42, sm: 52 },
                height: { xs: 42, sm: 52 },
                bgcolor: "#fff",
                color: "#2563eb",
                fontWeight: 800,
              }}
            >
              {(selectedTenant.tenantName || "T")
                .charAt(0)
                .toUpperCase()}
            </Avatar>

            <Box minWidth={0}>
              <Typography
                fontWeight={800}
                fontSize={{ xs: 16, sm: 20 }}
                color="white"
                noWrap
              >
                {selectedTenant.tenantName || "Tenant"}
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,.8)" }}
              >
                Tenant details
              </Typography>
            </Box>
          </Stack>

          <Chip
            label={statusLabel}
            size="small"
            color={statusColor}
            sx={{ fontWeight: 700 }}
          />
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          p: { xs: 2, sm: 3 },
          overflowY: "auto",
        }}
      >
        <Stack spacing={2} mt={1}>
          <InfoCard
            icon={<PhoneIcon fontSize="small" color="primary" />}
            title="Mobile Number"
          >
            <Typography fontWeight={700}>
              {selectedTenant.mobileNumber || "Not provided"}
            </Typography>
          </InfoCard>

          <InfoCard
            icon={
              <CalendarMonthIcon
                fontSize="small"
                color="primary"
              />
            }
            title="Stay Duration"
          >
            <Typography fontWeight={700}>
              {formatDate(selectedTenant.startDate)}
              {" → "}
              {formatDate(selectedTenant.endDate)}
            </Typography>

            {!selectedTenant.endDate && (
              <Typography
                variant="caption"
                color="text.secondary"
              >
                No end date is recorded; this stay is treated as
                ongoing.
              </Typography>
            )}
          </InfoCard>

          <InfoCard
            icon={<HomeIcon fontSize="small" color="primary" />}
            title="Accommodation"
          >
            <Chip
              label={accommodation}
              color={
                selectedTenant.accommodationType === "FOOD_ONLY"
                  ? "warning"
                  : "primary"
              }
              sx={{ fontWeight: 700, borderRadius: 2 }}
            />
          </InfoCard>

          <InfoCard title="Room / Location">
            <Typography fontWeight={700}>{location}</Typography>

            {selectedTenant.hostelName && (
              <Typography
                variant="body2"
                color="text.secondary"
                mt={0.5}
              >
                {selectedTenant.hostelName}
              </Typography>
            )}
          </InfoCard>
        </Stack>

        {/* Monthly Payment History */}
        <Box mt={4}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            gap={1}
            mb={2}
          >
            <Box>
              <Typography variant="h6" fontWeight={800}>
                Monthly Payment History
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Invoice and payment status by billing month
              </Typography>
            </Box>

            <Chip
              size="small"
              label={`${monthlyFees.length} ${
                monthlyFees.length === 1 ? "month" : "months"
              }`}
              variant="outlined"
            />
          </Stack>

          {monthlyFees.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 3,
                textAlign: "center",
                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
              >
                No monthly invoice details available.
              </Typography>
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 3 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 800 }}>
                      Billing Month
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 800, whiteSpace: "nowrap" }}
                    >
                      Invoice Amount
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 800, whiteSpace: "nowrap" }}
                    >
                      Paid Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {monthlyFees.map((fee, index) => {
                    const payment = getPaymentChip(
                      fee.paymentStatus
                    );

                    return (
                      <TableRow
                        key={`${fee.billMonth ?? "month"}-${index}`}
                        hover
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                          >
                            {formatBillMonth(fee.billMonth)}
                          </Typography>
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {formatAmount(fee.invoiceTotalAmount)}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {formatAmount(fee.paidAmount)}
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={payment.label}
                            color={payment.color}
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => setSelectedTenant(null)}
          sx={{
            borderRadius: 3,
            py: 1.2,
            fontWeight: 800,
            textTransform: "none",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantDialog;
